import { memberSubscriptionsQuery } from "@/api/reporting";
import { DataTable } from "@/components/data-table/DataTable";
import { DataTableBody } from "@/components/data-table/DataTableBody";
import { DataTableHeader } from "@/components/data-table/DataTableHeader";
import { StyledLink } from "@/components/link/StyledLink";
import { PointsAmount } from "@/components/PointsAmount";
import { PageWrapper } from "@/components/ui/PageWrapper";
import { formatDate } from "@common/utils/date";
import { Box, Select } from "@givenwell/components";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { fallback, number, object, optional, pipe, string, toMinValue } from "valibot";

export const Route = createFileRoute("/admin/reports/$countryCode/member-subscriptions")({
  component: MemberSubscriptionsReportPage,
  validateSearch: object({
    status: fallback(optional(string(), ""), ""),
    page: fallback(pipe(optional(number(), 1), toMinValue(1)), 1),
    "page-size": fallback(pipe(optional(number(), 100), toMinValue(10)), 100),
  }),
  beforeLoad: () => ({
    breadcrumb: "Member Subscriptions",
  }),
});

function MemberSubscriptionsReportPage() {
  const search = Route.useSearch();
  const { countryCode } = Route.useParams();
  const { status } = search;
  const navigate = useNavigate();

  const query = useQuery(memberSubscriptionsQuery(countryCode, status as any));

  function handleStatusChange(status: string) {
    navigate({
      to: ".",
      params: true,
      search: { ...search, status },
    });
  }

  return (
    <PageWrapper title="Member — Subscriptions">
      <Box css={{ spaceY: 16 }}>
        <DataTable>
          <DataTableHeader hideSearch hideToggle>
            <Select value={status} onValueChange={handleStatusChange} css={{ maxW: 300 }}>
              <option value="">All</option>
              <option value="pending">Pending</option>
              <option value="active">Active</option>
              <option value="cancelled">Cancelled</option>
              <option value="cancellation_pending">Cancellation Pending</option>
            </Select>
          </DataTableHeader>
          <DataTableBody
            data={query.data ?? []}
            dataKey="subscription_id"
            status={query.status}
            rounded="bottom"
            columns={[
              {
                header: "Member",
                cell: r => `${r.member_given_name} ${r.member_family_name}`,
              },
              {
                header: "Supporter",
                cell: r => (
                  <StyledLink to={`/admin/supporters/$supporterId`} params={{ supporterId: r.supporter_id! }}>
                    {r.supporter}
                  </StyledLink>
                ),
              },
              {
                header: "Supplier",
                cell: "supplier",
              },
              { header: "Listng", cell: "listing" },
              { header: "Variant", cell: "variant" },
              {
                header: "Start Date",
                cell: row => formatDate(row.start_date),
              },
              {
                header: "Next Payment",
                cell: row => formatDate(row.next_payment_date),
              },
              {
                header: "Status",
                cell: "status",
              },
              {
                header: "Amount",
                cell: row => (
                  <PointsAmount amount={row.recurring_token_amount} tokenCurrency={row.token_currency_code} />
                ),
                css: {
                  textAlign: "right",
                },
              },
              {
                header: "Balance",
                cell: row => (
                  <PointsAmount amount={row.member_token_balance ?? 0} tokenCurrency={row.token_currency_code} />
                ),
                css: {
                  textAlign: "right",
                },
              },
            ]}
          />
        </DataTable>
      </Box>
    </PageWrapper>
  );
}
